import { Alert, Box, Icon, Link, Text } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { generatePath } from 'react-router';
import { PaymentBillingEngineDto } from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import {
  useAfterPaymentRedirection,
  useGetRecurrentPaymentConcepts,
  usePayorderToPay,
} from 'domains/Billing/Checkout/hooks';
import {
  trackingBillingCreditCardFailureChangeMethod,
  trackingBillingFailedPaymentAlertPayClick,
} from 'domains/Billing/Checkout/tracking';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { PaymentStatusAlertProps } from '../../../../PaymentStatusAlert';
import GoToPayPlanLink from '../../../GoToPayPlanLink';
import { PayButton } from '../../../PayButton';

/**
 * When this component is used outside of the New Checkout (i.e. external context), we don't
 * want to show the messages for these error codes, and instead show the generic message.
 */
const errorCodeExternalContextBlacklist = [
  'invalidCardNumber',
  'invalidExpirationDate',
  'invalidSecurityCode',
  'invalidCardGeneric',
];

const ERROR_CODE_MAP = {
  ['cc_rejected_insufficient_amount']: 'insufficientFunds',
  ['cc_rejected_call_for_authorize']: 'callRequired',
  ['cc_rejected_high_risk']: 'highRisk',
  ['cc_rejected_blacklist']: 'highRisk',
  ['cc_rejected_bad_filled_card_number']: 'invalidCardNumber',
  ['cc_rejected_bad_filled_date']: 'invalidExpirationDate',
  ['cc_rejected_bad_filled_security_code']: 'invalidSecurityCode',
  ['cc_rejected_bad_filled_other']: 'invalidCardGeneric',
};

const failedReasonCodeMap = (
  payment: PaymentBillingEngineDto | undefined,
  isExternalContext: boolean,
  errorCode?: string,
) => {
  if (payment?.isInvalidOperator) return 'invalidOperator';
  const code = payment?.errorCode || errorCode;
  const error = (code && ERROR_CODE_MAP[code]) || 'genericError';
  return isExternalContext && errorCodeExternalContextBlacklist.includes(error)
    ? 'genericError'
    : error;
};

interface PaymentStatusAlertFailedProps extends PaymentStatusAlertProps {
  payment: PaymentBillingEngineDto | undefined;
  isFromRecurrentPayment: boolean;
  isFromCreditCardForm?: boolean;
  errorCode?: string;
  requiredChangeMethod?: boolean;
}

export default function PaymentStatusAlertFailed({
  payment,
  isExternalContext = false,
  isFromRecurrentPayment,
  isFromCreditCardForm = false,
  requiredChangeMethod = true,
  errorCode,
  showPaymentButton,
  concept,
  externalReference,
}: PaymentStatusAlertFailedProps) {
  const { goTo } = useNavegate();
  const t = useTranslationBilling('checkout.paymentStatusAlert.creditCard');
  const isMobileDevice = useIsMobileDevice();
  const code = failedReasonCodeMap(payment, isExternalContext, errorCode);
  const { recurrentPaymentForPlanActivated } = useGetRecurrentPaymentConcepts();

  const { passOnActionToPath } = useAfterPaymentRedirection();
  const { payOrderId, mainConcept } = usePayorderToPay();
  const handleChangeMethod = () => {
    mainConcept && trackingBillingCreditCardFailureChangeMethod(mainConcept);

    goTo(
      passOnActionToPath(
        generatePath(CHECKOUT_ROUTES.choosePaymentMethodForPayOrder, {
          payOrderId,
        }),
      ),
    );
  };

  // we add the suffix "Mobile"to the translation key when we are in the mobile application to change the content
  const mobileSuffix = isMobileDevice ? 'Mobile' : '';
  const recurrentPaymentPath = isFromRecurrentPayment
    ? `recurrentPaymentActivated${mobileSuffix}`
    : `recurrentPaymentDisabled${mobileSuffix}`;
  const isHighRisk = code === 'highRisk';
  const externalLink = isFromCreditCardForm ? null : (
    <Link as="a" href={t('failed.highRiskHelpLink')}>
      {t('moreInformation')}
      <Icon source={<ExternalLinkIcon />} />
    </Link>
  );
  const onClick = () => {
    trackingBillingFailedPaymentAlertPayClick(
      !!recurrentPaymentForPlanActivated,
    );
  };
  const button = (
    <GoToPayPlanLink as={isHighRisk ? 'button' : 'link'} onClick={onClick} />
  );

  return (
    <Alert appearance="danger" title={t('failed.title')}>
      <Text>{t(`failed.${recurrentPaymentPath}.${code}`)}</Text>
      {isHighRisk && !isExternalContext && externalLink}
      {isHighRisk && isExternalContext && (
        <Box display="flex" flexDirection="row" gap="2">
          {button}
          {externalLink}
        </Box>
      )}
      {isExternalContext && !isHighRisk && button}
      {isFromCreditCardForm && requiredChangeMethod && (
        <Link as="button" onClick={handleChangeMethod}>
          {t('failed.changeMethod')}
        </Link>
      )}
      {showPaymentButton && concept && (
        <PayButton concept={concept} externalReference={externalReference} />
      )}
    </Alert>
  );
}
